// Versão modificada da EdicaoRelatorioAM para suportar rascunho

import { PageLoader } from "@/components/PageLoader";
import { useEstabelecimento } from "@/hooks/useEstabelecimento";
import { useFormulario } from "@/hooks/useFormulario";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { useRelatorioAM } from "@/hooks/useRelatorioAM";
import { IFormValuesRelatorioAM } from "@/interfaces/IFormValuesRelatorioAM";
import { IRelatorioAM } from "@/interfaces/IRelatorioAM";
import { ISecaoQuestionario } from "@/interfaces/ISecaoQuestionario";
import { converterFileToBase64 } from "@/utils/convertFileToBase64";
import { cpfMask } from "@/utils/cpfMask";
import { formatMessageError } from "@/utils/formatMessageError";
import {
  ArrowLeftOutlined,
  CheckOutlined,
  FilePdfOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import {
  App,
  Button,
  Col,
  Divider,
  Form,
  Grid,
  Input,
  Result,
  Row,
  Typography,
  UploadFile,
} from "antd";
import { RcFile } from "antd/lib/upload";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useReactToPrint } from "react-to-print";
import { v4 as uuid } from "uuid";
import { ImpresssaoRelatorioAM } from "../../_components/ImpresssaoRelatorioAM";
import { AssinaturaRelatorioAM } from "../_components/AssinaturaRelatorioAM";
import { FormDadosGerais } from "../_components/FormDadosGerais";
import { FormFiscalizacoes } from "../_components/FormFiscalizacoes";
import { QuestionarioRelatorioAM } from "../_components/QuestionarioRelatorioAM";

export function EdicaoRelatorioAM() {
  const { isOnline } = useNetworkStatus();
  const { offlineId } = useParams<{ offlineId: string }>();
  const relatorioRef = useRef<HTMLDivElement>(null);
  const [form] = Form.useForm<IFormValuesRelatorioAM>();
  const screens = Grid.useBreakpoint();
  const navigate = useNavigate();
  const [relatorioMinuta, setRelatorioMinuta] = useState<IRelatorioAM | null>(
    null
  );
  const { getRelatorioAMByOfflineId } = useRelatorioAM();
  const relatorioOffline = getRelatorioAMByOfflineId(Number(offlineId));
  const [secoesQuestionario, setSecoesQuestionario] = useState<
    ISecaoQuestionario[]
  >([]);
  const [isLoadingQuestionario, setIsLoadingQuestionario] = useState(false);
  const { listaEstabelecimentos } = useEstabelecimento();
  const { listaFormularios } = useFormulario();
  const {
    submitRelatorioAMApi,
    updateRelatorioAM,
    updateRelatorioAMApi,
    validateFinalizadoRelatorio,
  } = useRelatorioAM();
  const { notification, message } = App.useApp();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingItems, setIsLoadingItems] = useState(true);

  const handleBack = () => {
    navigate(-1);
  };

  const criarArquivoComNovoNome = (arquivo: RcFile, novoNome: string): File => {
    return new File([arquivo], novoNome, {
      type: arquivo.type,
      lastModified: arquivo.lastModified,
    });
  };

  const buildRelatorioRequest = (
    values: IFormValuesRelatorioAM
  ): IRelatorioAM | null => {
    const formularioSelected = listaFormularios.find(
      (formulario) => formulario.id === values.formulario.id
    );

    const estabelecimentoSelected = listaEstabelecimentos.find(
      (estabelecimento) => estabelecimento.id === values.estabelecimento.id
    );

    const pessoaInspecaoSelected =
      estabelecimentoSelected?.pessoasInspecao.find(
        (pessoa) => pessoa.id === values.inspetor.id
      );

    const proprietarioSelected =
      estabelecimentoSelected?.sociosProprietarios.find(
        (proprietario) => proprietario.id === values.proprietario.id
      );

    if (
      !proprietarioSelected ||
      !pessoaInspecaoSelected ||
      !estabelecimentoSelected ||
      !formularioSelected ||
      !relatorioOffline
    ) {
      notification.error({
        message: "Erro ao salvar o relatório",
        description:
          "Não foi possível encontrar todas as informações selecionadas.",
      });
      return null;
    }

    return {
      offlineId: relatorioOffline.offlineId,
      id: relatorioOffline.id, // Preservar ID se existir
      formulario: formularioSelected,
      estabelecimento: estabelecimentoSelected,
      inspetor: pessoaInspecaoSelected,
      proprietario: proprietarioSelected,
      responsavelTecnico: values.responsavelTecnico || "",
      fiscalizacoes: (values.fiscalizacoes || []).map((fiscalizacao) => ({
        data: fiscalizacao.data?.format("YYYY-MM-DD") || null,
        horaFinal: fiscalizacao.horaFinal?.format("HH:mm") || null,
        horaInicial: fiscalizacao.horaInicial?.format("HH:mm") || null,
        placa: fiscalizacao.placa || "",
      })),
      observacao: values.observacao || "",
      acaoFiscal: values.acaoFiscal || "",
      conclusao: values.conclusao || "",
      situacao: "PENDENTE",
      assinaturas: (values.assinaturas || []).map((assinante) => ({
        nome: assinante.nome || "",
        cpf: (assinante.cpf || "").replace(/\D/g, ""),
        matricula: assinante.matricula || "",
        funcao: assinante.funcao || "",
        externo: assinante.externo || false,
        assinatura: assinante.assinatura || null,
        statusAssinatura: assinante.statusAssinatura || "PENDENTE",
      })),
      dataMonitoria: relatorioOffline?.dataMonitoria,
      itens: (values.questionarios || [])
        .filter((quest) => quest.pergunta?.id)
        .map((quest) => {
          const pergunta = formularioSelected.perguntas.find(
            (p) => p.pergunta.id === quest.pergunta.id
          );

          const alternativa = pergunta?.pergunta.alternativas.find(
            (a) => a.id === quest.alternativa?.id
          );

          // Buscar o UUID existente no relatorioOffline ou criar um novo
          const itemExistente = relatorioOffline.itens?.find(
            (item) => item.pergunta.id === quest.pergunta.id
          );
          const uuidItem = itemExistente?.uuid || uuid();

          return {
            uuid: uuidItem,
            dataResposta: quest.dataResposta
              ? quest.dataResposta
              : "1970-01-01",
            resposta: quest.observacao || "",
            secao: pergunta!.secao,
            alternativa: alternativa || null,
            pergunta: pergunta!.pergunta,
            status: "AGUARDANDO_PLANO_DE_ACAO",
            peso: pergunta?.peso!,
            anexos: (quest.anexo || []).map((anexo) => ({
              nomeArquivo: `${uuidItem}_${anexo.name}`,
              nomeUpload: anexo.uid,
            })),
          };
        }),
    };
  };

  const handleSave = async () => {
    if (!relatorioOffline) return;
    try {
      const values = form.getFieldsValue();

      // Validações mínimas para gravar (igual ao novo relatório)
      if (
        !values.formulario?.id ||
        !values.estabelecimento?.id ||
        !values.inspetor?.id ||
        !values.proprietario?.id ||
        !values.responsavelTecnico?.trim()
      ) {
        notification.error({
          message: "Campos obrigatórios não preenchidos",
          description:
            "Por favor, preencha pelo menos o formulário, estabelecimento, inspetor, proprietário e responsável técnico.",
        });
        return;
      }

      const request = buildRelatorioRequest(values);
      if (!request) return;

      if (validateFinalizadoRelatorio(request)) {
        request.situacao = "FINALIZADO";
      }

      if (!isOnline) {
        // Processar anexos offline
        const itensComAnexosBase64 = await Promise.all(
          request.itens.map(async (item) => {
            if (item.anexos.length > 0) {
              const anexosRcFile: RcFile[] =
                values.questionarios
                  ?.find((q) => q.pergunta.id === item.pergunta.id)
                  ?.anexo?.map((anexo) => anexo.originFileObj as RcFile) || [];

              if (anexosRcFile.length > 0) {
                const anexosBase64 = await Promise.all(
                  anexosRcFile.map((anexo) => converterFileToBase64(anexo))
                );
                item.anexos.forEach((anexo, index) => {
                  if (anexosBase64[index]) {
                    anexo.arquivoBase64 = anexosBase64[index];
                  }
                });
              }
            }
            return item;
          })
        );

        request.itens = itensComAnexosBase64;
        updateRelatorioAM({ ...request, isUpdated: false });

        notification.success({
          message: "Relatório gravado com sucesso",
          description:
            "O relatório foi salvo como rascunho. Você pode continuar editando posteriormente.",
        });

        saveAs(
          new Blob([JSON.stringify(request)], {
            type: "text/plain;charset=utf-8",
          }),
          "backup_relatorio_auditoria_monitoria-" + Date.now() + ".txt"
        );

        handleBack();
        return;
      }

      message.loading({
        content: "Gravando relatório...",
        key: "savingRelatorio",
        duration: 0,
      });

      setIsSubmitting(true);

      try {
        const formData = new FormData();

        const requestOnline = {
          ...request,
          itens: request.itens.filter((item) => item.alternativa), // Filtrar itens sem alternativa selecionada
        };

        formData.append(
          "ramRequest",
          new Blob([JSON.stringify(requestOnline)], {
            type: "application/json",
          })
        );

        // Processar anexos
        const anexosRequest: { arquivo: File; nomeArquivo: string }[] = [];

        for (const item of request.itens) {
          if (item.anexos.length > 0) {
            const anexosRcFile: RcFile[] =
              values.questionarios
                ?.find((q) => q.pergunta.id === item.pergunta.id)
                ?.anexo?.map((anexo) => anexo.originFileObj as RcFile) || [];

            anexosRcFile.forEach((anexoRc, index) => {
              const nomeArquivo =
                item.anexos[index]?.nomeArquivo ||
                `${item.uuid}_${anexoRc.name}`;
              anexosRequest.push({
                arquivo: criarArquivoComNovoNome(anexoRc, nomeArquivo),
                nomeArquivo,
              });
            });
          }
        }

        anexosRequest.forEach((anexo) => {
          formData.append("anexos", anexo.arquivo, anexo.nomeArquivo);
        });

        let result;
        if (relatorioOffline.id) {
          result = await updateRelatorioAMApi(formData, relatorioOffline.id);
        } else {
          result = await submitRelatorioAMApi(formData);
        }

        notification.success({
          message: "Relatório gravado com sucesso",
          description: "O relatório foi gravado no servidor.",
        });

        message.destroy("savingRelatorio");
        setIsSubmitting(false);

        updateRelatorioAM({ ...request, id: result.id, isUpdated: true });
        handleBack();
      } catch (error) {
        console.error(error);
        setIsSubmitting(false);
        message.destroy("savingRelatorio");
        const messageError = formatMessageError(error);
        notification.error({
          message: "Não foi possível gravar o relatório",
          description: messageError,
        });
      }
    } catch (error) {
      console.error("Erro ao gravar:", error);
      notification.error({
        message: "Erro ao gravar",
        description: "Por favor, verifique os campos preenchidos.",
      });
    }
  };

  const handleFinalize = async (values: IFormValuesRelatorioAM) => {
    if (!relatorioOffline) return;

    // validacao se a fiscalizacao tem data, hora inicial, hora final e placa
    const fiscalizacoesInvalidas = (values.fiscalizacoes || []).filter(
      (fiscalizacao) =>
        !fiscalizacao.data ||
        !fiscalizacao.horaInicial ||
        !fiscalizacao.horaFinal ||
        !fiscalizacao.placa
    );
    if (fiscalizacoesInvalidas.length > 0) {
      notification.error({
        message: "Não foi possivel finalizar o relatório",
        description:
          "Existem fiscalizações sem data, hora inicial, hora final ou placa. Por favor, preencha todas as fiscalizações.",
      });
      return;
    }

    // Validações completas para finalizar
    if (!values.questionarios || values.questionarios.length === 0) {
      notification.error({
        message: "Não foi possível finalizar o relatório",
        description: "Nenhum questionário respondido.",
      });
      return;
    }

    // Validar se todas as perguntas obrigatórias foram respondidas
    const perguntasNaoRespondidas = values.questionarios.filter(
      (quest) => !quest.alternativa?.id
    );

    if (perguntasNaoRespondidas.length > 0) {
      notification.error({
        message: "Não foi possível finalizar o relatório",
        description:
          "Existem perguntas sem alternativa selecionada. Por favor, responda todas as perguntas.",
      });
      return;
    }

    // Validar assinaturas
    if (!values.assinaturas || values.assinaturas.length === 0) {
      notification.error({
        message: "Não foi possível finalizar o relatório",
        description: "É necessário adicionar pelo menos uma assinatura.",
      });
      return;
    }

    const assinaturaInvalida = values.assinaturas.find(
      (assinante) =>
        assinante.statusAssinatura === "ASSINADO_DIGITALMENTE" &&
        !assinante.assinatura
    );

    if (assinaturaInvalida) {
      notification.error({
        message: "Não foi possível finalizar o relatório",
        description:
          "Existem assinaturas digitais sem a assinatura preenchida. Por favor, verifique.",
      });
      return;
    }

    // Validar campos obrigatórios
    if (!values.conclusao?.trim()) {
      notification.error({
        message: "Não foi possível finalizar o relatório",
        description: "O campo conclusão é obrigatório.",
      });
      return;
    }

    const request = buildRelatorioRequest(values);
    if (!request) return;

    request.situacao = "FINALIZADO";

    if (!isOnline) {
      const itensComAnexosBase64 = await Promise.all(
        request.itens.map(async (item) => {
          if (item.anexos.length > 0) {
            const anexosRcFile: RcFile[] = values.questionarios
              ?.find((q) => q.pergunta.id === item.pergunta.id)
              ?.anexo.map((anexo) => anexo.originFileObj as RcFile) as RcFile[];
            const anexosBase64 = await Promise.all(
              anexosRcFile.map((anexo) => converterFileToBase64(anexo))
            );
            item.anexos.forEach((anexo, index) => {
              anexo.arquivoBase64 = anexosBase64[index];
            });
          }
          return item;
        })
      );

      request.itens = itensComAnexosBase64;
      updateRelatorioAM({ ...request, isUpdated: false });

      notification.success({
        message: "Relatório finalizado com sucesso",
        description:
          "O relatório foi finalizado e salvo localmente até ser sincronizado com o servidor.",
      });

      saveAs(
        new Blob([JSON.stringify(request)], {
          type: "text/plain;charset=utf-8",
        }),
        "backup_relatorio_auditoria_monitoria-" + Date.now() + ".txt"
      );

      handleBack();
      return;
    }

    message.loading({
      content: "Finalizando relatório...",
      key: "finalizingRelatorio",
    });

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append(
        "ramRequest",
        new Blob([JSON.stringify(request)], {
          type: "application/json",
        })
      );

      // Processar anexos
      const anexosItem = await Promise.all(
        request.itens.map(async (item) => {
          if (item.anexos.length > 0) {
            const anexosRcFile: RcFile[] = values.questionarios
              ?.find((q) => q.pergunta.id === item.pergunta.id)
              ?.anexo.map((anexo) => anexo.originFileObj as RcFile) as RcFile[];

            return anexosRcFile.map((anexoRc, index) => {
              const novoNome = item.anexos[index].nomeArquivo;
              return {
                arquivo: criarArquivoComNovoNome(anexoRc, novoNome),
                nomeArquivo: novoNome,
              };
            });
          }
          return [];
        })
      );

      const anexosRequest = anexosItem.flatMap((item) => item);
      anexosRequest.forEach((anexo) => {
        formData.append("anexos", anexo.arquivo as File, anexo.nomeArquivo);
      });

      let result;
      if (relatorioOffline.id) {
        result = await updateRelatorioAMApi(formData, relatorioOffline.id);
      } else {
        result = await submitRelatorioAMApi(formData);
      }

      notification.success({
        message: "Relatório finalizado com sucesso",
        description:
          "O relatório foi finalizado e sincronizado com o servidor.",
      });

      message.destroy("finalizingRelatorio");
      setIsSubmitting(false);

      updateRelatorioAM({ ...request, id: result.id, isUpdated: true });
      handleBack();
    } catch (error) {
      console.error(error);
      setIsSubmitting(false);
      message.destroy("finalizingRelatorio");
      const messageError = formatMessageError(error);
      notification.error({
        message: "Não foi possível finalizar o relatório",
        description: messageError,
      });
    }
  };

  const handleGeneratePreview = async () => {
    try {
      const values = form.getFieldsValue();

      const request = buildRelatorioRequest(values);
      if (!request) return;

      const requestPreview: IRelatorioAM = {
        ...request,
        itens: request.itens.map((item) => ({
          ...item,
          anexos: [], // Preview sem anexos
        })),
      };

      setRelatorioMinuta(requestPreview);
    } catch (error) {
      console.error("Validation Failed:", error);
      notification.error({
        message: "Não foi possível gerar a prévia",
        description: "Por favor, verifique os campos preenchidos.",
      });
    }
  };

  const converterBase64ParaUploadFile = (
    nomeArquivo: string,
    dadosBase64: string,
    uid?: string
  ): UploadFile => {
    // [código da função mantido igual]

    const [cabecalho, conteudoBase64] = dadosBase64?.includes(",")
      ? dadosBase64.split(",")
      : ["data:application/octet-stream;base64", dadosBase64];

    const tipoMime =
      cabecalho.match(/data:([^;]+)/)?.[1] || "application/octet-stream";

    const bytesDecodificados = atob(conteudoBase64);
    const arrayBytes = new Uint8Array(bytesDecodificados.length);

    for (let i = 0; i < bytesDecodificados.length; i++) {
      arrayBytes[i] = bytesDecodificados.charCodeAt(i);
    }

    const arquivo = new File([arrayBytes], nomeArquivo, { type: tipoMime });

    return {
      uid: uid || `arquivo-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      name: nomeArquivo.split("_")[1],
      status: "done",
      size: arquivo.size,
      type: arquivo.type,
      originFileObj: arquivo as any,
      url: URL.createObjectURL(arquivo),
      thumbUrl: tipoMime.startsWith("image/")
        ? URL.createObjectURL(arquivo)
        : undefined,
    };
  };

  const handlePrint = useReactToPrint({
    contentRef: relatorioRef,
    documentTitle: "ram_minuta",
    suppressErrors: true,
    preserveAfterPrint: true,
    onAfterPrint: () => setRelatorioMinuta(null),
  });

  useEffect(() => {
    if (relatorioMinuta) {
      handlePrint();
    }
  }, [relatorioMinuta, handlePrint]);

  useEffect(() => {
    document.title =
      "Inspeção Mobile - Edição de Relatório de Auditoria e Monitoria";

    setTimeout(() => {
      if (relatorioOffline) {
        // [código de inicialização mantido igual]
        const secoes = relatorioOffline.formulario.perguntas.reduce(
          (acc, pergunta) => {
            const secaoId = pergunta.secao.id;

            if (
              pergunta.pergunta.visivel === false ||
              !pergunta.pergunta.ativo
            ) {
              return acc;
            }

            if (!acc[secaoId]) {
              acc[secaoId] = {
                secao: pergunta.secao.nome,
                perguntas: [],
              };
            }

            acc[secaoId].perguntas.push({
              id: pergunta.pergunta.id,
              ordem: pergunta.ordem,
              nome: pergunta.pergunta.nome,
              dica: pergunta.pergunta.dica,
              tipoResposta: pergunta.pergunta.tipoResposta,
              peso: pergunta.peso,
              dias: pergunta.dias,
              alternativas: pergunta.pergunta.alternativas,
              ativo: pergunta.pergunta.ativo,
              visivel: pergunta.pergunta.visivel,
            });

            return acc;
          },
          {} as Record<
            number,
            { secao: string; perguntas: ISecaoQuestionario["perguntas"] }
          >
        );

        const secoesArray = Object.values(secoes);
        setSecoesQuestionario(secoesArray);

        const questionariosComRespostas: IFormValuesRelatorioAM["questionarios"] =
          [];

        secoesArray.forEach((secao) => {
          secao.perguntas
            .filter((pergunta) => pergunta.visivel && pergunta.ativo)
            .sort((a, b) => a.ordem - b.ordem)
            .forEach((pergunta) => {
              const respostaExistente = relatorioOffline.itens?.find(
                (q) => q.pergunta.id === pergunta.id
              );

              const anexosWithBase64 = respostaExistente?.anexos.filter(
                (anexo) => anexo.arquivoBase64
              );

              const anexos =
                anexosWithBase64?.map((anexo) =>
                  converterBase64ParaUploadFile(
                    anexo.nomeArquivo,
                    anexo.arquivoBase64!,
                    anexo.nomeUpload
                  )
                ) || [];

              questionariosComRespostas.push({
                pergunta: { id: pergunta.id },
                alternativa: respostaExistente?.alternativa?.id
                  ? { id: respostaExistente.alternativa.id }
                  : undefined,
                observacao: respostaExistente?.resposta || "",
                anexo: anexos,
                // Só define dataResposta se há alternativa selecionada
                dataResposta: respostaExistente?.alternativa?.id
                  ? respostaExistente?.dataResposta || undefined
                  : undefined,
              });
            });
        });

        form.setFieldsValue({
          estabelecimento: { id: relatorioOffline.estabelecimento.id },
          formulario: { id: relatorioOffline.formulario.id },
          inspetor: { id: relatorioOffline.inspetor.id },
          proprietario: { id: relatorioOffline.proprietario.id },
          responsavelTecnico: relatorioOffline.responsavelTecnico,
          acaoFiscal: relatorioOffline.acaoFiscal,
          conclusao: relatorioOffline.conclusao,
          observacao: relatorioOffline.observacao || undefined,
          questionarios: questionariosComRespostas,
          fiscalizacoes:
            relatorioOffline.fiscalizacoes.map((fisc) => ({
              data: (fisc.data && dayjs(fisc.data, "YYYY-MM-DD")) || undefined,
              horaFinal:
                (fisc.horaFinal && dayjs(fisc.horaFinal, "HH:mm")) || undefined,
              horaInicial:
                (fisc.horaInicial && dayjs(fisc.horaInicial, "HH:mm")) ||
                undefined,
              placa: fisc.placa,
            })) || [],
          assinaturas:
            relatorioOffline.assinaturas?.length > 0
              ? relatorioOffline.assinaturas.map((assinante) => ({
                  nome: assinante.nome,
                  cpf: cpfMask(assinante.cpf),
                  matricula: assinante.matricula,
                  funcao: assinante.funcao,
                  externo: assinante.externo,
                  assinatura: assinante.assinatura || undefined,
                  statusAssinatura: assinante.statusAssinatura,
                }))
              : [{ matricula: undefined }],
        });
      }
      setIsLoadingItems(false);
    }, 500);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoadingItems) {
    return <PageLoader />;
  }

  if (!relatorioOffline) {
    return (
      <Result
        status="404"
        title="Relatório de Auditoria e Monitoria não encontrado"
      />
    );
  }

  return (
    <>
      <Typography.Title
        level={screens.xs ? 4 : 3}
        style={{ textAlign: "center" }}
      >
        Edição Relatório de Auditoria e Monitoria
      </Typography.Title>

      <Divider />

      <Form
        form={form}
        layout="vertical"
        scrollToFirstError
        onFinish={handleFinalize}
        onFinishFailed={() => {
          notification.error({
            message: "Erro ao finalizar o relatório",
            description: "Por favor, verifique os campos obrigatórios.",
          });
        }}
      >
        <FormDadosGerais
          form={form}
          setSecoesQuestionario={setSecoesQuestionario}
          setIsLoadingQuestionario={setIsLoadingQuestionario}
          isEdit
        />

        <FormFiscalizacoes />

        <Divider />

        <Form.Item
          label="Conclusão"
          name="conclusao"
          rules={[
            { required: true, message: "Campo obrigatório para finalizar" },
          ]}
        >
          <Input.TextArea placeholder="Informe a conclusão do relatório" />
        </Form.Item>

        <Form.Item label="Ação Fiscal" name="acaoFiscal">
          <Input.TextArea placeholder="Informe a ação fiscal do relatório" />
        </Form.Item>

        <QuestionarioRelatorioAM
          secoesQuestionario={secoesQuestionario}
          form={form}
          isLoadingQuestionario={isLoadingQuestionario}
        />

        <AssinaturaRelatorioAM form={form} />

        <Divider />

        <Row justify="center" gutter={[10, 10]} style={{ marginTop: 48 }}>
          <Col>
            <Button
              htmlType="button"
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
              disabled={isSubmitting}
              style={{ width: 180 }}
            >
              Voltar
            </Button>
          </Col>

          <Col>
            <Button
              icon={<FilePdfOutlined />}
              htmlType="button"
              style={{ width: 180 }}
              onClick={handleGeneratePreview}
              disabled={isSubmitting}
            >
              Gerar Prévia
            </Button>
          </Col>

          <Col>
            <Button
              icon={<SaveOutlined />}
              htmlType="button"
              style={{ width: 180 }}
              onClick={handleSave}
              disabled={isSubmitting}
              loading={isSubmitting}
              type="primary"
              ghost
            >
              Gravar
            </Button>
          </Col>

          <Col>
            <Button
              icon={<CheckOutlined />}
              type="primary"
              htmlType="submit"
              style={{ width: 180 }}
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              Finalizar
            </Button>
          </Col>
        </Row>
      </Form>

      <div style={{ display: "none" }}>
        <ImpresssaoRelatorioAM
          ref={relatorioRef}
          item={relatorioMinuta}
          marcadagua="MINUTA"
        />
      </div>
    </>
  );
}
